import type { Me } from '@myrewards/contracts';
import { useSuspenseQuery } from '@tanstack/react-query';
import type { ReactElement, ReactNode } from 'react';
import { peoplePhotos } from '../assets';
import { spendQuery, spendSearch, spendView } from '../api/insights';
import { useLibrary, useView } from '../insights/frame';
import { canOpen, yearSpan } from '../insights/labels';
import { SpendDashboard, spendHero } from '../insights/SpendPage';
import { Hero, PageBody, type HeroAction } from '../shell';
import { greeting } from './labels';
import s from './Home.module.css';

/**
 * F1 · Home for someone whose only staff role is Finance (APP-054): the greeting over this year's
 * spend, Waiting on you on top as on every Home, then the Spend dashboard. The dashboard is the
 * Spend page's own body; its filters live in Home's address, and "Open in Insights" carries them.
 */
export function FinanceHome({
  me,
  waiting,
  spot = null,
}: {
  me: Me;
  waiting: ReactElement;
  /** APP-232: Spot awards in progress, for someone on the Spot Committee. */
  spot?: ReactNode;
}): ReactElement {
  const [view, setView] = useView(spendView, spendSearch);
  const { data: spend } = useSuspenseQuery(spendQuery(view));
  const library = useLibrary();
  const hero = spendHero(spend, view, me);
  const open: HeroAction = {
    label: 'Open Spend in Insights',
    href: `/insights/spend${spendSearch(view)}`,
    tone: 'cyan',
  };
  const actions: readonly [HeroAction] | readonly [HeroAction, HeroAction] = canOpen(
    library,
    'budgets_history',
  )
    ? [open, { label: 'Budgets & history', href: '/insights/budgets' }]
    : [open];
  return (
    <>
      <Hero
        key={me.person.id}
        eyebrow={`Finance · Spend · ${yearSpan(spend.fiscalYear, spend.asOf)}`}
        eyebrowTone="cyan"
        title={`${greeting(new Date())}, ${me.person.greetingName}.`}
        // The heading is a greeting; the browser tab says which page this is (QA-022).
        documentTitle="Home"
        sub={hero.sub}
        actions={actions}
        stats={hero.stats}
        photos={peoplePhotos}
        titleTestId="home-title"
      />
      <PageBody>
        <div className={s.stack}>
          {waiting}
          {spot}
          <SpendDashboard spend={spend} view={view} setView={setView} />
        </div>
      </PageBody>
    </>
  );
}
